/**
 * AnimationManager - Singleton class to manage animation playback.
 *
 * This class is responsible for:
 * - Registering and tracking animations
 * - Driving animation updates via a tick-based loop
 * - Supporting different play styles (OneShot, Loop, Rocker, Interactive)
 * - Managing animation lifecycle (play, pause, cancel)
 *
 * This is a direct port of the C++ AnimationManager from tabanim.
 *
 */

import { AnimationPlayInfo, AnimationCompletionFunc } from './AnimationPlayInfo';
import { AnimationPlayStyle } from './AnimationPlayStyle';
import { Animation } from './BaseAnimation';

export class AnimationManager {
  /** Singleton instance */
  private static instance: AnimationManager;

  private currentAnimations: AnimationPlayInfo[] = [];

  /** Animations queued for removal (processed at end of tick) */
  private animationRemovalQueue: AnimationPlayInfo[] = [];

  private isTicking = false;

  /** Animation frame request ID (for cancelling) */
  private animationFrameId?: number;

  /** Threshold for interactive animation updates (avoid excessive updates) */
  private static readonly INTERACTIVE_THRESHOLD = 0.001;

  /** Pause between rocker phases in ms (matches C++ m_replayPause = 0.40s) */
  private static readonly REPLAY_PAUSE_MS = 400;

  /** Max rewind duration in ms (matches C++ m_rewindDuration = 0.35s) */
  private static readonly REWIND_DURATION_MS = 350;

  /** Animation fraction start values */
  private static readonly ANIMATION_START_FRACTION = 0.0;
  private static readonly ANIMATION_END_FRACTION = 1.0;

  /** Sentinel values for animation state */
  private static readonly SENTINEL_START_TIME = -1;
  private static readonly SENTINEL_ORIGINAL_DURATION = -1;

  private constructor() {
    // Intentionally empty for singleton pattern
  }

  public static getInstance(): AnimationManager {
    if (!AnimationManager.instance) {
      AnimationManager.instance = new AnimationManager();
    }
    return AnimationManager.instance;
  }

  /**
   * Plays an animation from start (fraction 0) to finish (fraction 1)
   * over the given duration.
   *
   * @param animation - The animation to play
   * @param totalDuration - Duration in milliseconds
   * @param completion - Optional callback when animation completes
   * @param playStyle - Play style (default: OneShot)
   */
  public playAnimation(
    animation: Animation,
    totalDuration: number,
    completion?: AnimationCompletionFunc,
    playStyle: AnimationPlayStyle = AnimationPlayStyle.OneShot
  ): void {
    // Match C++ AnimationManager::PlayAnimation (AnimationManager.cpp:507-510)
    // Rocker (replay) starts at fraction 1.0 playing backward: TO→FROM then FROM→TO
    const isReplaying = playStyle === AnimationPlayStyle.Rocker;
    const startFraction = isReplaying ? AnimationManager.ANIMATION_END_FRACTION : AnimationManager.ANIMATION_START_FRACTION;
    const playForward = !isReplaying;
    this.playAnimationFrom(animation, startFraction, totalDuration, completion, playStyle, playForward);
  }

  /**
   * Plays an animation from a specific fraction to the end (fraction 1).
   *
   * @param animation - The animation to play
   * @param fromFraction - Starting fraction [0, 1]
   * @param totalDuration - Duration in milliseconds
   * @param completion - Optional callback when animation completes
   * @param playStyle - Play style (default: OneShot)
   * @param playForward - Direction to play (default: true)
   */
  public playAnimationFrom(
    animation: Animation,
    fromFraction: number,
    totalDuration: number,
    completion?: AnimationCompletionFunc,
    playStyle: AnimationPlayStyle = AnimationPlayStyle.OneShot,
    playForward = true
  ): void {
    // Cancel any existing playback of this animation
    this.cancelAnimation(animation);

    // Process removal queue immediately to ensure old instance is removed
    this.removeQueuedAnimations();

    // Clamp fromFraction to valid range
    const startFraction = Math.max(0, Math.min(1, fromFraction));

    animation.setFraction(startFraction);

    // C++ uses sentinel startTime=-1 for Rocker so UpdateRockerAnimation can initialize
    // it on the first tick (after duration adjustment). Other styles use real time.
    const isRocker = playStyle === AnimationPlayStyle.Rocker;
    const currentTime = isRocker ? AnimationManager.SENTINEL_START_TIME : performance.now();
    const playInfo = new AnimationPlayInfo(animation, currentTime, totalDuration, startFraction, playForward, playStyle, completion);

    // Add to current animations
    this.currentAnimations.push(playInfo);

    // NOTE: Do NOT start internal tick loop - C# AnimationClock is responsible for ticking
    // The WASM version doesn't have its own tick loop, it's driven externally
  }

  /**
   * Plays an animation backwards from a specific fraction to 0.
   *
   * @param animation - The animation to play
   * @param fromFraction - Starting fraction [0, 1]
   * @param totalDuration - Duration in milliseconds
   * @param completion - Optional callback when animation completes
   * @param playStyle - Play style (default: OneShot)
   */
  public playAnimationBackwards(
    animation: Animation,
    fromFraction: number,
    totalDuration: number,
    completion?: AnimationCompletionFunc,
    playStyle: AnimationPlayStyle = AnimationPlayStyle.OneShot
  ): void {
    this.playAnimationFrom(animation, fromFraction, totalDuration, completion, playStyle, false);
  }

  /**
   * The animation's completion callback will NOT be called.
   */
  public cancelAnimation(animation: Animation): void {
    const playInfo = this.findAnimationInfo(animation);
    if (playInfo) {
      playInfo.wasCancelled = true;
      this.animationRemovalQueue.push(playInfo);
    }
  }

  public cancelAllAnimations(): void {
    this.animationRemovalQueue.push(...this.currentAnimations);
  }

  public isAnimationPlaying(animation: Animation): boolean {
    return this.findAnimationInfo(animation) !== undefined;
  }

  public getNumCurrentAnimations(): number {
    return this.currentAnimations.length;
  }

  public getCurrentAnimations(): readonly AnimationPlayInfo[] {
    return this.currentAnimations;
  }

  public startTicking(): void {
    if (!this.isTicking) {
      this.isTicking = true;
      this.tickLoop();
    }
  }

  /**
   * Current animations remain registered but won't update.
   */
  public stopTicking(): void {
    this.isTicking = false;
    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }
  }

  /**
   * Called automatically via requestAnimationFrame.
   */
  private tickLoop(): void {
    if (!this.isTicking) {
      return;
    }

    const currentTime = performance.now();
    this.tick(currentTime);

    // Continue the loop
    this.animationFrameId = requestAnimationFrame(() => this.tickLoop());
  }

  public tick(timeStamp: number): void {
    // Update each animation based on its play style
    for (const playInfo of this.currentAnimations) {
      switch (playInfo.playStyle) {
        case AnimationPlayStyle.OneShot:
          this.updateOneShotAnimation(playInfo, timeStamp);
          break;
        case AnimationPlayStyle.Loop:
          this.updateLoopAnimation(playInfo, timeStamp);
          break;
        case AnimationPlayStyle.Rocker:
          this.updateRockerAnimation(playInfo, timeStamp);
          break;
        case AnimationPlayStyle.Interactive:
          this.updateInteractiveAnimation(playInfo, timeStamp);
          break;
      }
    }

    // Process completion callbacks
    this.callCompletionBlocksForCompleted();

    // Remove any animations in the removal queue
    this.removeQueuedAnimations();

    // NOTE: Don't call stopTicking() here - C# AnimationClock is responsible for stopping
    // The WASM version doesn't control its own tick loop, it's driven externally
  }

  private updateOneShotAnimation(playInfo: AnimationPlayInfo, timeStamp: number): void {
    const elapsed = timeStamp - playInfo.startTime;
    const t = playInfo.duration > 0 ? elapsed / playInfo.duration : 1;

    if (t >= 1.0) {
      // Animation complete
      const endFraction = playInfo.playForwards ? 1.0 : 0.0;
      playInfo.animation.setFraction(endFraction);
      this.animationRemovalQueue.push(playInfo);
    } else {
      // Animation in progress
      const fraction = playInfo.playForwards
        ? playInfo.startFraction + t * (1.0 - playInfo.startFraction)
        : playInfo.startFraction - t * playInfo.startFraction;
      playInfo.animation.setFraction(fraction);
    }
  }

  private updateLoopAnimation(playInfo: AnimationPlayInfo, timeStamp: number): void {
    const elapsed = timeStamp - playInfo.startTime;
    let t = playInfo.duration > 0 ? (elapsed % playInfo.duration) / playInfo.duration : 0;

    // Clamp t to [0, 1]
    t = Math.max(0, Math.min(1, t));

    playInfo.animation.setFraction(t);
    playInfo.playCount = Math.floor(elapsed / playInfo.duration);
  }

  /**
   * Phase-based rocker animation matching C++ AnimationManager::UpdateRockerAnimation.
   *
   * C++ behavior (AnimationManager.cpp:370-428):
   * - Each direction (forward/backward) is one "phase"
   * - playCount increments when a phase completes and direction toggles
   * - Stops after playCount > 1 (i.e., 2 phases: forward + backward)
   * - 400ms pause between phases (m_replayPause)
   * - Backward phase capped to 350ms (m_rewindDuration) if original is longer
   *
   * Now matches C++ exactly: starts backward (TO→FROM, 350ms) → pause → forward (FROM→TO) → stop.
   * C++ uses sentinel values (-1 for startTime and originalDuration) to handle
   * the initial backward phase duration cap on the first tick.
   */
  private updateRockerAnimation(playInfo: AnimationPlayInfo, timeStamp: number): void {
    if (playInfo.duration === 0) {
      return;
    }

    // C++ lines 378-390: Duration adjustment based on direction
    // This runs every tick, not just during transitions
    if (!playInfo.playForwards && playInfo.originalDuration === AnimationManager.SENTINEL_ORIGINAL_DURATION) {
      // First tick of backward phase: save original duration, cap to rewind speed
      playInfo.originalDuration = playInfo.duration;
      if (playInfo.originalDuration > AnimationManager.REWIND_DURATION_MS) {
        playInfo.duration = AnimationManager.REWIND_DURATION_MS;
      }
    } else if (playInfo.playForwards) {
      // Forward phase: use original duration (C++ line 389: originalDuration * m_replaySpeed)
      playInfo.duration = playInfo.originalDuration;
    }

    // C++ lines 392-405: Sentinel startTime initialization
    if (playInfo.startTime === AnimationManager.SENTINEL_START_TIME) {
      playInfo.startTime = timeStamp;
      if (playInfo.startFraction >= 0) {
        playInfo.startTime -= (AnimationManager.ANIMATION_END_FRACTION - playInfo.startFraction) * playInfo.duration;
      }
    }

    let elapsedTime = timeStamp - playInfo.startTime;

    // Phase completion check (C++ lines 410-423)
    if (elapsedTime > playInfo.duration) {
      elapsedTime -= playInfo.duration;
      playInfo.playForwards = !playInfo.playForwards;
      // Add pause between phases (C++ line 415: m_replayPause = 0.40s)
      playInfo.startTime = timeStamp - elapsedTime + AnimationManager.REPLAY_PAUSE_MS;
      playInfo.playCount++;

      if (playInfo.playCount > 1) {
        // 2 phases complete (backward + forward), stop (C++ lines 418-422)
        playInfo.originalDuration = AnimationManager.SENTINEL_ORIGINAL_DURATION; // Reset sentinel for next replay (C++ line 421)
        this.animationRemovalQueue.push(playInfo);
      }

      // Recalculate elapsedTime with updated startTime (C++ line 407 runs before line 425)
      elapsedTime = timeStamp - playInfo.startTime;
    }

    // Calculate fraction based on current direction (C++ lines 425-427)
    let fraction = playInfo.playForwards
      ? elapsedTime / playInfo.duration
      : AnimationManager.ANIMATION_END_FRACTION - elapsedTime / playInfo.duration;

    // Clamp to [0, 1] - fraction can be out of range during the pause period
    // (C++ Animation::SetFraction also clamps: Animation.cpp:15)
    fraction = Math.max(AnimationManager.ANIMATION_START_FRACTION, Math.min(AnimationManager.ANIMATION_END_FRACTION, fraction));
    playInfo.animation.setFraction(fraction);
  }

  private updateInteractiveAnimation(playInfo: AnimationPlayInfo, _timeStamp: number): void {
    playInfo.animation.setFraction(playInfo.currentInteractiveFraction);
  }

  /**
   * Uses a threshold to avoid excessive updates.
   */
  public setInteractiveAnimationFraction(animation: Animation, newFraction: number): void {
    const playInfo = this.findAnimationInfo(animation);
    if (!playInfo || playInfo.playStyle !== AnimationPlayStyle.Interactive) {
      return;
    }

    // Use threshold to avoid excessive updates
    const delta = Math.abs(newFraction - playInfo.lastInteractiveFraction);
    if (delta >= AnimationManager.INTERACTIVE_THRESHOLD) {
      playInfo.currentInteractiveFraction = newFraction;
      playInfo.lastInteractiveFraction = newFraction;
      animation.setFraction(newFraction);
    }
  }

  /**
   * Only calls callbacks for animations that completed naturally, not cancelled ones.
   */
  private callCompletionBlocksForCompleted(): void {
    for (const playInfo of this.animationRemovalQueue) {
      // Only call completion if the animation wasn't manually cancelled
      if (playInfo.completionFunc && !playInfo.isInteractiveCancel && !playInfo.wasCancelled) {
        playInfo.completionFunc(playInfo.animation);
      }
    }
  }

  private removeQueuedAnimations(): void {
    if (this.animationRemovalQueue.length === 0) {
      return;
    }

    // Remove animations from current animations list
    const toRemove = new Set(this.animationRemovalQueue);
    this.currentAnimations = this.currentAnimations.filter((playInfo) => !toRemove.has(playInfo));

    // Clear the removal queue
    this.animationRemovalQueue = [];
  }

  private findAnimationInfo(animation: Animation): AnimationPlayInfo | undefined {
    return this.currentAnimations.find((info) => info.animation === animation);
  }
}
